/**
 * Cart drawer add-ons, bundles and the free-gift picker.
 *
 * Adds a variant straight to the cart without leaving the drawer, then
 * re-renders the drawer sections the same way product-form does.
 *
 * Three kinds of row use this. An add-on adds one more of an accessory, and
 * once it is in the cart its button becomes the way back out. A bundle row
 * carries data-addon-mode="set" and a data-addon-quantity: it empties the cart
 * and leaves exactly that many bottles in it, and its tick is transient — a
 * receipt, not a state. A gift row is an add-on row wearing different words:
 * once the cart holds enough bottles the add-ons card becomes the gift picker,
 * and choosing a second gift swaps the first out rather than stacking beside
 * it, because only one of them is going to be free.
 *
 * None of the money is the theme's doing. The bundle saving and the free gift
 * are both automatic discounts in Shopify; this file only ever puts the right
 * things in the cart and describes what Shopify is about to do to them.
 */
class CartAddons extends HTMLElement {
  static IN_CART_LABEL = 'In your cart — take one out';
  static TRANSIENT_MS = 1800;
  // Half of the label's own fade, so the words change at the bottom of the dip
  // where they cannot be read mid-swap. Matches the CSS.
  static SWAP_MS = 150;

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.variantsInCart = new Set();
    this.giftLines = [];

    // Bundle rows read "Get 3 bottles", add-on rows read "Add to cart". Both
    // get overwritten by errors, spinners and gift wording, so keep the
    // original to put back.
    this.querySelectorAll('[data-addon-label]').forEach((label) => {
      label.dataset.defaultLabel = label.textContent.trim();
      label.dataset.normalLabel = label.dataset.defaultLabel;
    });

    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('change', this.onChange.bind(this));

    // Liquid may have rendered this card straight into gift mode, so the
    // wording is put right before the first request comes back.
    this.refreshLabels();

    if (Number(this.dataset.cartCount) > 0) this.syncWithCart();

    if (!CartAddons.subscribed && typeof subscribe === 'function') {
      CartAddons.subscribed = true;
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        document.querySelectorAll('cart-addons').forEach((node) => node.syncWithCart());
      });
    }
  }

  get cartDrawer() {
    return document.querySelector('cart-drawer');
  }

  get giftEnabled() {
    return this.dataset.giftEnabled === 'true';
  }

  /**
   * The products this card offers, by product rather than by variant, so a
   * gift picked in one size still counts as that gift.
   */
  get giftProductIds() {
    return Array.from(this.querySelectorAll('.cart-addons__item[data-product-id]')).map((row) =>
      String(row.dataset.productId)
    );
  }

  /**
   * The zero-priced variant of this row's product, if it has one.
   *
   * A gift is not a discount. It is a variant that costs nothing, so the free
   * one and the paid one are two different things in the cart and Shopify is
   * never asked to make a judgement about either. Nothing else in the theme has
   * to be right for the customer to be charged the right amount.
   */
  giftVariantFor(button) {
    const row = button.closest('.cart-addons__item');
    return row ? row.dataset.giftVariantId || '' : '';
  }

  /**
   * The variants that make this row read as "in your cart" right now.
   *
   * In gift mode that is the free one alone. A brush someone paid for earlier
   * must not stop them choosing a brush as their gift, and the button on this
   * card is about the gift while the card is the gift picker — the paid line
   * still has its own remove button down in the drawer.
   */
  activeVariantIds(button) {
    const gift = this.giftVariantFor(button);
    if (gift && this.classList.contains('is-gift')) return [gift];
    return [String(button.dataset.variantId || '')].filter(Boolean);
  }

  /**
   * Which one this click should add. The free variant only while the card is
   * in gift mode, and only if the product actually has one — a product with no
   * zero-priced variant falls back to being sold, never quietly given away at
   * full price.
   */
  variantFor(button) {
    if (!this.classList.contains('is-gift')) return String(button.dataset.variantId);
    return this.giftVariantFor(button) || String(button.dataset.variantId);
  }

  onClick(event) {
    // The quiet paid link is checked first: it sits inside the same row as the
    // real button, and it must never be mistaken for one.
    const buy = event.target.closest('[data-addon-buy]');
    if (buy && !buy.disabled) {
      // Once the bought one is in the cart this button is the way back out of
      // it, exactly like the button above it. It only ever answers for the
      // paid variant, so the free one is never what comes out.
      return buy.classList.contains('is-added')
        ? this.removeVariant(buy, [String(buy.dataset.variantId)])
        : this.buyVariant(buy);
    }

    const button = event.target.closest('[data-addon-add]');
    if (!button || button.disabled) return;

    // Already in the cart, so this button is the way back out rather than a
    // way to a second one. Bundle rows keep their flash-and-forget tick and
    // are left out of it.
    if (button.classList.contains('is-added') && !button.hasAttribute('data-addon-transient')) {
      return this.removeVariant(button);
    }

    this.addVariant(button);
  }

  /** Keep price, availability and the button in step with the chosen variant. */
  onChange(event) {
    const select = event.target.closest('[data-addon-select]');
    if (!select) return;

    const item = select.closest('.cart-addons__item');
    const option = select.options[select.selectedIndex];
    const button = item.querySelector('[data-addon-add]');
    const available = option.dataset.available === 'true';

    button.dataset.variantId = option.value;
    button.disabled = !available;
    this.setAddedState(
      button,
      this.activeVariantIds(button).some((id) => this.variantsInCart.has(id))
    );

    const label = button.querySelector('[data-addon-label]');
    label.dataset.normalLabel = available ? window.variantStrings.addToCart : window.variantStrings.soldOut;
    label.textContent = label.dataset.normalLabel;

    const prices = item.querySelector('[data-addon-price]');
    if (prices) {
      const onSale = option.dataset.onSale === 'true';
      const free = prices.querySelector('[data-addon-free]');
      const freeText = free ? free.textContent.trim() : 'Free';

      prices.innerHTML = `<span class="cart-addons__price">${option.dataset.price}</span>${
        onSale ? `<s class="cart-addons__price-compare">${option.dataset.comparePrice}</s>` : ''
      }<span class="cart-addons__free" data-addon-free>${freeText}</span>`;
    }

    const buy = item.querySelector('[data-addon-buy]');
    if (buy) {
      buy.dataset.variantId = option.value;
      buy.disabled = !available;
      this.setBuyState(buy, this.variantsInCart.has(String(option.value)));
      const buyPrice = buy.querySelector('[data-addon-buy-price]');
      if (buyPrice) buyPrice.textContent = option.dataset.price;
    }

    // Rebuilding the row wiped the gift wording along with everything else.
    this.refreshLabels();
  }

  /** The drawer repaints from whichever cart route ran, so all of these share this. */
  get sectionsPayload() {
    const drawer = this.cartDrawer;
    if (!drawer) return null;

    return {
      sections: drawer.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    };
  }

  addLine(variantId, quantity) {
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity);

    const payload = this.sectionsPayload;
    if (payload) {
      formData.append('sections', payload.sections);
      formData.append('sections_url', payload.sections_url);
    }
    config.body = formData;

    return fetch(`${routes.cart_add_url}`, config).then((response) => response.json());
  }

  /**
   * A tier is not an adjustment, it is a decision: pressing "3 bottles" empties
   * the cart and leaves exactly three bottles in it.
   *
   * Adding on top of whatever was already there is where this goes wrong. Two
   * bottle colours, a gift from an earlier threshold, an accessory bought at a
   * different tier — every one of those changes which Shopify discounts fire,
   * and the card ends up promising a total the checkout will not honour. In
   * both directions: free things nobody earned, or a saving that quietly does
   * not apply. Starting from empty means the cart can only ever be the thing
   * the button said.
   *
   * The colour survives. Whichever bottle was in the cart first is the one that
   * comes back, so choosing black and then pressing a tier does not hand back
   * three blue ones.
   */
  setCartToBottles(quantity, fallbackVariantId) {
    return fetch(`${routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cart) => {
        // Whichever bottle was picked first is the colour that comes back.
        const bottleId = String(this.dataset.bundleProductId || '');
        const firstBottle = cart.items.find((item) => String(item.product_id) === bottleId);
        const keep = firstBottle ? String(firstBottle.variant_id) : String(fallbackVariantId);

        // Empty it, then put the bottles in. Setting quantities line by line
        // meant reasoning about which line held what and trusting the sums to
        // come out; clearing first means there is nothing to reason about. The
        // cart after this click can only be the number on the button.
        const clear = fetchConfig('javascript');
        clear.body = JSON.stringify({});

        return fetch(`${routes.cart_url}/clear.js`, clear).then(() => this.addLine(keep, quantity));
      });
  }

  /**
   * Choosing the gift. One free thing, one of it, whatever was there before.
   *
   * This is not an add. Every free line on the card goes to nothing first and
   * the chosen one is then set to exactly one, so the cart after the click is
   * the same whether it is the first press or the tenth. An add could stack —
   * press "pick this" ten times and ten free brushes go out of the door — and
   * that is a hole worth closing at the point where the cart is written rather
   * than by asking every button to remember how many times it was pressed.
   *
   * Anything with a price on it is left alone. A brush someone actually bought
   * is not a gift and is not ours to take away.
   */
  chooseGift(button) {
    const variantId = this.variantFor(button);
    const giftIds = this.giftProductIds;

    // Read the cart again rather than trusting the copy from the last event.
    return fetch(`${routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cart) => {
        const updates = {};

        cart.items.forEach((item) => {
          if (!giftIds.includes(String(item.product_id))) return;
          if (item.price !== 0) return;
          updates[item.variant_id] = 0;
        });

        updates[variantId] = 1;

        const config = fetchConfig('javascript');
        config.body = JSON.stringify({ updates, ...this.sectionsPayload });

        return fetch(`${routes.cart_update_url}`, config).then((response) => response.json());
      });
  }

  /** True when this click is a gift being chosen rather than a thing being bought. */
  isGiftPick(button) {
    return this.classList.contains('is-gift') && Boolean(this.giftVariantFor(button));
  }

  /**
   * One free thing, one of it — whatever the cart turns up holding.
   *
   * The buttons above refuse to hand out a second, but a cart is not only ever
   * written by this card. A stale tab, a cart permalink, or a cart filled in
   * before this rule existed can all arrive with ten free brushes in it. So the
   * cart is read and put right rather than trusted.
   *
   * The same pass takes the gift back when the bottles that earned it leave.
   * It costs nothing, so nothing else would ever take it back: three bottles,
   * pick the free brush, drop two bottles, and the brush would go out of the
   * door for a one-bottle order. What the card promises is "free with three",
   * and that has to still be true at the till.
   *
   * It publishes rather than re-rendering the drawer, because this can happen
   * while the drawer is shut and a cart correcting itself is no reason to open
   * it in someone's face.
   */
  enforceGiftLimit(cart, earned) {
    if (CartAddons.fixingGift) return;

    const giftIds = this.giftProductIds;
    const free = cart.items.filter(
      (item) => giftIds.includes(String(item.product_id)) && item.price === 0
    );
    if (!free.length) return;

    // One of whichever came first, and only while it is still earned.
    const updates = {};
    free.forEach((item, index) => {
      const allowed = earned && index === 0 ? 1 : 0;
      if (item.quantity !== allowed) updates[item.variant_id] = allowed;
    });
    if (!Object.keys(updates).length) return;

    CartAddons.fixingGift = true;

    const config = fetchConfig('javascript');
    config.body = JSON.stringify({ updates });

    fetch(`${routes.cart_update_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) return;
        if (typeof publish === 'function') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-addons', cartData: response });
        }
      })
      .catch(() => {})
      .finally(() => {
        CartAddons.fixingGift = false;
      });
  }

  addVariant(button) {
    // In gift mode this is the zero-priced variant, not the one on the button.
    const variantId = this.variantFor(button);
    if (!variantId) return;

    const quantity = Number(button.dataset.addonQuantity) || 1;

    button.classList.add('is-loading');
    button.disabled = true;

    let request;
    if (button.dataset.addonMode === 'set') {
      request = this.setCartToBottles(quantity, variantId);
    } else if (this.isGiftPick(button)) {
      request = this.chooseGift(button);
    } else {
      request = this.addLine(variantId, quantity);
    }

    request
      .then((response) => {
        button.classList.remove('is-loading');

        if (response.status) {
          button.disabled = false;
          button.querySelector('[data-addon-label]').textContent = response.description || response.message;
          return;
        }

        this.markAdded(button, variantId);
        this.refreshLabels();
        this.finishCartRequest(response, variantId);
      })
      .catch((error) => {
        console.error(error);
        button.classList.remove('is-loading');
        button.disabled = false;
      });
  }

  /**
   * Buying one outright, alongside the free one. Always a plain add: this is
   * the button for "both", so it must never swap anything out.
   */
  buyVariant(button) {
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    button.disabled = true;
    button.classList.add('is-busy');

    this.addLine(variantId, 1)
      .then((response) => {
        button.classList.remove('is-busy');
        button.disabled = false;
        if (response.status) return;

        this.variantsInCart.add(String(variantId));
        this.setBuyState(button, true);
        this.finishCartRequest(response, variantId);
      })
      .catch((error) => {
        console.error(error);
        button.classList.remove('is-busy');
        button.disabled = false;
      });
  }

  /** Tell the rest of the page, then let the drawer repaint itself. */
  finishCartRequest(response, variantId) {
    if (typeof publish === 'function') {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-addons',
        productVariantId: variantId,
        cartData: response,
      });
    }

    const drawer = this.cartDrawer;
    if (drawer) {
      drawer.classList.remove('is-empty');
      drawer.renderContents(response);
    }
  }

  /**
   * Take one out. One unit, never the whole line: two of something in the cart
   * were two decisions, and only one of them is being undone by this click.
   */
  removeVariant(button, only) {
    // Take out whichever of this row's variants is the one making it read as
    // added — the free one in gift mode, the bought one otherwise. The paid
    // button passes its own single variant, because it answers for that one
    // and nothing else.
    const candidates = only || this.activeVariantIds(button);
    const paid = button.hasAttribute('data-addon-buy');
    const busyClass = paid ? 'is-busy' : 'is-loading';
    let variantId = candidates[0];

    button.classList.add(busyClass);
    button.disabled = true;

    fetch(`${routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cart) => {
        const line = cart.items.find((item) => candidates.includes(String(item.variant_id)));
        if (line) variantId = String(line.variant_id);

        const config = fetchConfig('javascript');

        config.body = JSON.stringify({
          updates: { [variantId]: line ? Math.max(0, line.quantity - 1) : 0 },
          ...this.sectionsPayload,
        });

        return fetch(`${routes.cart_update_url}`, config).then((response) => response.json());
      })
      .then((response) => {
        button.classList.remove(busyClass);
        button.disabled = false;
        if (response.status) return;

        this.variantsInCart.delete(variantId);
        if (paid) this.setBuyState(button, false);
        else this.setAddedState(button, false);
        this.refreshLabels();
        this.finishCartRequest(response, variantId);
      })
      .catch((error) => {
        console.error(error);
        button.classList.remove(busyClass);
        button.disabled = false;
      });
  }

  markAdded(button, addedVariantId) {
    const label = button.querySelector('[data-addon-label]');

    button.disabled = false;
    label.textContent = label.dataset.defaultLabel || window.variantStrings.addToCart;

    // A bundle's tick is a receipt for the click, not a state — the bottle was
    // in the cart before it and stays there after, so leaving the row lit would
    // read as "this tier is what you have". Flash it and let it go.
    if (button.hasAttribute('data-addon-transient')) {
      this.setAddedState(button, true);
      clearTimeout(button.revertTimer);
      button.revertTimer = setTimeout(() => this.setAddedState(button, false), CartAddons.TRANSIENT_MS);
      return;
    }

    this.variantsInCart.add(String(addedVariantId || button.dataset.variantId));
    this.setAddedState(button, true);
  }

  setAddedState(button, added) {
    button.classList.toggle('is-added', added);
    if (added && !button.hasAttribute('data-addon-transient')) {
      button.setAttribute('aria-label', CartAddons.IN_CART_LABEL);
    } else {
      button.removeAttribute('aria-label');
    }
  }

  /**
   * The paid button's own added state. Separate from the one above because the
   * two buttons in a row are watching two different variants: the free one and
   * the bought one. Either can be in the cart without the other.
   */
  setBuyState(button, added) {
    button.classList.toggle('is-added', added);
    if (added) button.setAttribute('aria-label', CartAddons.IN_CART_LABEL);
    else button.removeAttribute('aria-label');
  }

  /**
   * Mark anything already sitting in the cart so the row reads as "added", and
   * work out whether the cart has earned its gift. Bundle rows are skipped:
   * they all point at the bottle, which is normally in the cart already, so
   * every tier would come up lit.
   */
  syncWithCart() {
    const buttons = this.querySelectorAll('[data-addon-add]:not([data-addon-transient])');
    if (!buttons.length) return;

    fetch(`${routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cart) => {
        this.variantsInCart = new Set(cart.items.map((item) => String(item.variant_id)));

        // Gift mode decides which variant a row is watching, so work that out
        // from this same cart before the buttons are told anything.
        this.readGiftState(cart);

        buttons.forEach((button) => {
          const added = this.activeVariantIds(button).some((id) => this.variantsInCart.has(id));
          this.setAddedState(button, added);
        });

        // The paid button answers for its own variant only.
        this.querySelectorAll('[data-addon-buy]').forEach((buy) => {
          this.setBuyState(buy, this.variantsInCart.has(String(buy.dataset.variantId)));
        });

        this.refreshLabels();
      })
      .catch(() => {});
  }

  /** Bottles, not items. Brushes and straws do not count towards the gift. */
  readGiftState(cart) {
    if (!this.giftEnabled) return;

    const bottleId = String(this.dataset.bundleProductId || '');
    const bottles = cart.items.reduce(
      (total, item) => (String(item.product_id) === bottleId ? total + item.quantity : total),
      0
    );
    const giftIds = this.giftProductIds;

    this.dataset.bottleCount = bottles;

    // A gift is a line that costs nothing. A brush someone paid for is not a
    // gift, so it must not make the card think a choice has been made.
    this.giftLines = cart.items.filter(
      (item) => giftIds.includes(String(item.product_id)) && item.price === 0
    );

    const earned = bottles >= Number(this.dataset.giftThreshold || 3);
    this.applyGiftMode(earned);
    this.refreshLabels();

    // Last line of defence, and the only one that answers for carts this card
    // did not write.
    this.enforceGiftLimit(cart, earned);
  }

  /**
   * Crossing the threshold is the moment this card exists at all.
   *
   * Below it there is nothing here. Accessories can be bought on the shop page
   * without any of this, so putting them beside a one-bottle cart only asks a
   * question nobody came to answer — and a column that says one thing at a time
   * is read, where a column that always says two is skimmed.
   *
   * How it arrives is the stylesheet's business: it grows out of the bottom
   * edge of the bundles card and settles underneath. All this does is say
   * which of the two states the card is in.
   */
  applyGiftMode(on) {
    if (this.classList.contains('is-gift') === on) return;

    this.classList.toggle('is-gift', on);

    const column = this.closest('.cart-upsell');
    if (column) column.classList.toggle('is-gift-mode', on);
  }

  /**
   * What every button on this card is allowed to say, worked out in one place
   * so no two paths can disagree about it.
   *
   * Order matters. Sold out beats everything. Anything in the cart offers the
   * way out next, because that is the only thing left worth doing to it from
   * here. Only then does the gift wording get a say.
   *
   * The card never names which item Shopify will make free when two of them
   * are in the cart, because that is settled at checkout and not by this card.
   * The Gift pill only appears while there is exactly one to point at.
   */
  refreshLabels() {
    const gift = this.classList.contains('is-gift');
    const chosen = this.giftLines.map((line) => String(line.product_id));

    if (this.giftEnabled) {
      // Drives the quiet paid button: before anything is chosen there is no
      // "instead" to offer, because everything on the card is free.
      this.classList.toggle('has-gift', gift && chosen.length > 0);
      this.classList.toggle('has-single-gift', gift && chosen.length === 1);

      this.querySelectorAll('.cart-addons__item').forEach((row) => {
        row.classList.toggle('is-chosen-gift', gift && chosen.includes(String(row.dataset.productId)));
      });
    }

    this.querySelectorAll('[data-addon-add]:not([data-addon-transient])').forEach((button) => {
      const label = button.querySelector('[data-addon-label]');
      if (!label || button.classList.contains('is-loading')) return;

      let text = label.dataset.normalLabel || '';

      if (button.disabled) {
        // Sold out. Leave it saying so.
      } else if (button.classList.contains('is-added')) {
        text = this.dataset.removeLabel || 'Remove';
      } else if (gift && this.giftEnabled && this.giftVariantFor(button)) {
        // Only a row that has something free to give offers it. One without a
        // zero-priced variant keeps saying "Add to cart", because that is all
        // it can honestly do.
        text = chosen.length ? this.dataset.giftSwapLabel : this.dataset.giftChooseLabel;
      }

      this.setLabel(label, text);
    });
  }

  /**
   * Change a button's wording without it blinking.
   *
   * What is compared is what the button is actually showing, or is already on
   * its way to showing — not what it was last told. An error message sitting
   * in a label has to be swapped back out, and it would not be if this trusted
   * its own last instruction.
   */
  setLabel(label, text) {
    label.dataset.defaultLabel = text;

    const showing = label.dataset.pendingLabel || label.textContent.trim();
    if (showing === text) return;

    const button = label.closest('.cart-addons__button');
    if (!button || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      label.textContent = text;
      return;
    }

    label.dataset.pendingLabel = text;
    button.classList.add('is-swapping');

    clearTimeout(button.swapTimer);
    button.swapTimer = setTimeout(() => {
      label.textContent = text;
      delete label.dataset.pendingLabel;
      button.classList.remove('is-swapping');
    }, CartAddons.SWAP_MS);
  }
}

if (!customElements.get('cart-addons')) {
  customElements.define('cart-addons', CartAddons);
}
